import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DeliveryNotePreview = ({ data, isLoading }) => {
    const [searchParams] = useSearchParams();
    const preview = searchParams.get('preview');
    const [showPrintModal, setShowPrintModal] = useState(false);

    const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = MONTH_NAMES[date.getMonth()];
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null || amount === '') return '0.00';
        return parseFloat(amount).toFixed(3);
    };

    const getText = (value, preferredLang = 'en') => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            if (value[preferredLang]) return value[preferredLang];
            const first = Object.values(value)[0];
            return typeof first === 'string' ? first : '';
        }
        return String(value);
    };

    const getGroupedItems = () => {
        if (Array.isArray(data?.grouped_items) && data.grouped_items.length > 0) {
            return data.grouped_items;
        }

        const lineItems = Array.isArray(data?.items)
            ? data.items
            : (Array.isArray(data?.delivery_note_items) ? data.delivery_note_items : []);

        const groupedMap = new Map();
        lineItems.forEach((item) => {
            const key = item?.job_order_id || item?.job_order_number || 'unknown';
            if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                    job_order_id: item?.job_order_id || null,
                    job_order_number: getText(item?.job_order_number) || '-',
                    employee_name: getText(item?.employee_name) || '-',
                    items: [],
                });
            }
            groupedMap.get(key).items.push(item);
        });

        return Array.from(groupedMap.values()).map((group, index) => ({
            ...group,
            serial_number: index + 1,
        }));
    };

    useEffect(() => {
        if (!data) return;
        if (preview) setShowPrintModal(true);
    }, [preview, data]);

    useEffect(() => {
        if (!showPrintModal) return undefined;

        const cssHref = `${process.env.PUBLIC_URL || ''}/css/delivery_print.css`;
        const existing = document.getElementById('delivery-note-preview-css');
        if (existing) existing.remove();

        const link = document.createElement('link');
        link.id = 'delivery-note-preview-css';
        link.rel = 'stylesheet';
        link.href = cssHref;
        document.head.appendChild(link);

        return () => {
            document.getElementById('delivery-note-preview-css')?.remove();
        };
    }, [showPrintModal]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center p-4">
                <h5>No data available</h5>
            </div>
        );
    }

    const handlePrint = () => setShowPrintModal(true);
    const handleClosePrintModal = () => setShowPrintModal(false);

    return (
        <div className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Delivery Note Details</h5>
                </div>
                <div className="d-flex justify-content-end">
                    <button className="btn btn-primary btn-sm no-print" onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="fas fa-print"></i>
                        Print Preview
                    </button>
                </div>
            </div>

            {/* On-screen details */}
            <div className="table-responsive mt-2">
                <table className="table table-sm table-borderless" style={{ marginBottom: 0 }}>
                    <tbody>
                        <tr>
                            <td className="fw-bold">Delivery No:</td>
                            <td>{getText(data.invoice_number) || getText(data.number) || '-'}</td>
                            <td className="fw-bold">Delivery Date:</td>
                            <td>{formatDate(data.invoice_date || data.delivery_date)}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Customer:</td>
                            <td>{getText(data.customer_name) || getText(data.customer?.label) || '-'}</td>
                            <td className="fw-bold">Customer Code:</td>
                            <td>{getText(data.customer_code) || getText(data.customer?.code) || '-'}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Phone:</td>
                            <td>{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</td>
                        </tr>
                        <tr>
                            <td className="fw-bold">Address:</td>
                            <td colSpan="3">
                                {(getText(data.address1) || '')}
                                {getText(data.address2) ? `, ${getText(data.address2)}` : ''}
                                {getText(data.country?.name) || data.country?.label ? `, ${getText(data.country?.name) || data.country?.label}` : ''}
                            </td>
                        </tr>
                        <tr>
                            <td className="fw-bold">LPO No:</td>
                            <td>{getText(data.lpo_number?.label) || getText(data.lpo_number) || '-'}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* On-screen line items table */}
            <div className="table-responsive mt-4">
                {(() => {
                    const groupedItems = getGroupedItems();
                    return (
                        <table className="table table-bordered" style={{ tableLayout: 'auto', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th className="text-nowrap p-3" style={{ width: '5%' }}>No.</th>
                                    <th className="p-3" style={{ width: '18%' }}>Name &amp; Staff No.</th>
                                    <th className="p-3" style={{ width: '45%' }}>Description</th>
                                    <th className="text-nowrap p-3" style={{ width: '10%' }}>Qty.</th>
                                    <th className="text-nowrap p-3" style={{ width: '8%' }}>Unit</th>
                                    <th className="text-nowrap p-3" style={{ width: '14%' }}>Job No.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedItems && groupedItems.length > 0 ? (
                                    groupedItems.flatMap((group, groupIndex) => (
                                        (Array.isArray(group.items) ? group.items : []).map((row, rowIndex) => {
                                            const itemName = getText(row.item_description) || '-';
                                            const unit = getText(row.unit) || getText(row.item?.unit) || '-';
                                            const qty = row.quantity || row.qty || 0;
                                            const employeeName = getText(group.employee_name) || '-';
                                            const jobOrderNumber = getText(group.job_order_number) || getText(row.job_order_number) || '-';

                                            return (
                                                <tr key={`${group.job_order_id || group.job_order_number || groupIndex}-${row.id || rowIndex}`}>
                                                    <td className="text-nowrap p-3 text-center">
                                                        {rowIndex === 0 ? (group.serial_number || groupIndex + 1) : ''}
                                                    </td>
                                                    <td className="p-3 text-nowrap">
                                                        {rowIndex === 0 ? employeeName : ''}
                                                    </td>
                                                    <td className="p-3" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{itemName}</td>
                                                    <td className="text-end p-3">{qty}</td>
                                                    <td className="text-center p-3">{unit}</td>
                                                    <td className="text-nowrap p-3">{jobOrderNumber}</td>
                                                </tr>
                                            );
                                        })
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center p-3">No items available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    );
                })()}
            </div>

            {/* Print Modal */}
            {showPrintModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ backgroundColor: 'white', width: '90%', height: '90%', borderRadius: '8px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ padding: '15px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0 }}>Print Preview</h5>
                            <div>
                                <button className="btn btn-primary btn-sm me-2" onClick={() => {
                                    const printWindow = window.open('', '_blank');
                                    if (!printWindow) return;
                                    const baseUrl = window.location.origin + (process.env.PUBLIC_URL || '');
                                    const cssHref = `${baseUrl}/css/delivery_print.css`;
                                    const doPrint = () => {
                                        const modalContent = document.querySelector('.modal-print-content-deliverynote');
                                        if (modalContent) {
                                            const printContent = modalContent.innerHTML;
                                            const target = printWindow.document.getElementById('print-content');
                                            if (target) target.innerHTML = printContent;
                                        }
                                        printWindow.document.close();
                                        setTimeout(() => { 
                                            printWindow.print(); 
                                            printWindow.close(); 
                                        }, 100);
                                    };
                                    printWindow.document.write(`
                                        <html>
                                            <head>
                                                <title>Delivery Note Print</title>
                                                <link rel="stylesheet" href="${cssHref}" id="print-css-link" />
                                                <style>
                                                    html, body, #print-content, #container, #container * {
                                                        font-family: sans-serif !important;
                                                    }
                                                    #container, .print-content {
                                                        text-transform: uppercase;
                                                    }
                                                    @media print {
                                                        html, body { margin: 0 !important; padding: 0 !important; }
                                                        #print-content { padding-top: 150px !important; }
                                                        #container { margin: 0 auto !important; padding-top: 0 !important; }
                                                    }
                                                </style>
                                            </head>
                                            <body>
                                                <div id="print-content"></div>
                                            </body>
                                        </html>
                                    `);
                                    const linkEl = printWindow.document.getElementById('print-css-link');
                                    if (linkEl) {
                                        const fallback = setTimeout(doPrint, 3000);
                                        const done = () => { clearTimeout(fallback); doPrint(); };
                                        linkEl.onload = done;
                                        linkEl.onerror = done;
                                        if (linkEl.sheet) done();
                                    } else {
                                        doPrint();
                                    }
                                }}>
                                    <i className="fas fa-print me-1"></i>
                                    Print
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={handleClosePrintModal}>
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                            </div>
                        </div>
                        <div style={{ height: 'calc(100% - 70px)', overflow: 'auto', padding: '20px', fontFamily: 'sans-serif', textTransform: 'uppercase' }} className="modal-print-content-deliverynote">
                            <style>{`
                                .modal-print-content-deliverynote,
                                .modal-print-content-deliverynote * {
                                    font-family: sans-serif !important;
                                    text-transform: uppercase;
                                }
                            `}</style>
   
                            <div id="container" style={{ fontFamily: 'sans-serif', textTransform: 'uppercase' }}>
                                <div className="print-content" style={{ fontFamily: 'sans-serif', textTransform: 'uppercase' }}>
                                    <div className="clearfix"></div>
                                    <div className="title">
                                        <span>DELIVERY NOTE</span>
                                    </div>
                                    <section className="middle-box">
                                        <section id="client-info">
                                            <span className="client-name invoice_client_name">
                                                {getText(data.customer_name) || getText(data.customer?.label) || ''}
                                            </span>
                                            <span className="client-address">
                                                {getText(data.address2) ? `${getText(data.address2)}` : ''}
                                                {getText(data.country?.name) || data.country?.label ? `, ${getText(data.country?.name) || data.country?.label}` : ''}
                                            </span>
                                            <section id="client-contacts">
                                                <div>
                                                    <span className="client_label">PHONE : </span>
                                                    <span className="client_value">{getText(data.phone_number) || getText(data.customer?.phone) || '-'}</span>
                                                </div>
                                            </section>
                                        </section>

                                        <section id="invoice-info">
                                            <div>
                                                <span className="invoice_label">DELIVERY NUMBER : </span>
                                                <span className="invoice_value">{getText(data.invoice_number) || getText(data.number) || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">DELIVERY DATE : </span>
                                                <span className="invoice_value">{formatDate(data.invoice_date || data.delivery_date)}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">LPO NO:</span>
                                                <span className="invoice_value">{getText(data.lpo_number?.label) || getText(data.lpo_number) || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="invoice_label">LPO DATE:</span>
                                                <span className="invoice_value">{formatDate(data.lpo_date) || '-'}</span>
                                            </div>
                                        </section>
                                    </section>
                                    <div className="clearfix"></div>
                                    <section id="items">
                                        {(() => {
                                            const groupedItems = getGroupedItems();
                                            return (
                                                <table cellspacing="0" cellpadding="0" style={{ tableLayout: 'auto', width: '100%' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '5%', display: 'table-cell', textAlign: 'center' }}>NO.</th>
                                                            <th style={{ width: '18%' }}>NAME &amp; STAFF NO.</th>
                                                            <th style={{ width: '45%' }}>DESCRIPTION</th>
                                                            <th style={{ width: '10%' }}>QTY</th>
                                                            <th style={{ width: '8%' }}>UNIT</th>
                                                            <th style={{ width: '14%' }}>JOB NO.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {groupedItems && groupedItems.length > 0 ? (
                                                            groupedItems.flatMap((group, groupIndex) => (
                                                                (Array.isArray(group.items) ? group.items : []).map((row, rowIndex) => {
                                                                const serial = group.serial_number || groupIndex + 1;
                                                                const employeeName = getText(group.employee_name) || '-';
                                                                const itemName = getText(row.item_description) || '-';
                                                                const unit = getText(row.unit) || getText(row.item?.unit) || '-';
                                                                const qty = row.quantity || row.qty || 0;
                                                                const jobOrderNumber = getText(group.job_order_number) || getText(row.job_order_number) || '-';

                                                                return (
                                                                    <tr key={`${group.job_order_id || group.job_order_number || groupIndex}-${row.id || rowIndex}`} data-iterate="item">
                                                                        <td
                                                                            className="invoice_item_row_number"
                                                                            style={{ position: 'relative', display: 'table-cell', textAlign: 'center' }}
                                                                        >
                                                                            {rowIndex === 0 ? serial : ''}
                                                                        </td>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                                            <span className="">
                                                                                <b>{rowIndex === 0 ? employeeName : ''}</b>
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            <span className="">
                                                                                <b>{itemName}</b>
                                                                            </span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="invoice_item_quantity" contenteditable="true">{qty}</span>
                                                                        </td>
                                                                        <td>
                                                                            <span className="invoice_item_unit" contenteditable="true">{unit}</span>
                                                                        </td>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                                            <span className="invoice_item_vat " contenteditable="true">{jobOrderNumber}</span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="6" style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>No items available</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            );
                                        })()}
                                    </section>
                                    <div className="clearfix"></div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryNotePreview;

